/**
 * Etapa antes da assinatura de receituário com controlados/antimicrobianos:
 * mostra cada documento SNCR, se já há número para ele e oferece pedir a
 * numeração (gov.br) ou seguir com o receituário físico.
 */
import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  CLOUD_API_THEME,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';
import {
  isSncrNotificationTipo,
  SNCR_TIPO_LABELS,
  type SncrPlanDocument,
  type SncrPrescriptionPlan,
  type SncrReceitaTipo,
  type SncrRequestedItem,
} from '../../services/sncrService';

type DocStatus = 'numbered' | 'available' | 'missing';

const NR_MIN = 10;
const NR_MAX = 50;

/** Quanto pedir por tipo: RCE/RET vêm em lote fixo; notificações de 10 a 50. */
export function sncrRequestedFromPlan(plan: SncrPrescriptionPlan): SncrRequestedItem[] {
  return plan.needs
    .filter((n) => n.missing > 0)
    .map((n) => (
      isSncrNotificationTipo(n.receita_tipo)
        ? { receita_tipo: n.receita_tipo, quantidade: Math.min(NR_MAX, Math.max(NR_MIN, Math.ceil(n.missing / 10) * 10)) }
        : { receita_tipo: n.receita_tipo }
    ));
}

function docStatuses(plan: SncrPrescriptionPlan): DocStatus[] {
  const remaining = new Map<SncrReceitaTipo, number>();
  plan.needs.forEach((n) => remaining.set(n.receita_tipo, n.available));
  return plan.documents.map((doc) => {
    if (doc.reserved || doc.numero) return 'numbered';
    const left = remaining.get(doc.receita_tipo) || 0;
    if (left > 0) {
      remaining.set(doc.receita_tipo, left - 1);
      return 'available';
    }
    return 'missing';
  });
}

const STATUS_CHIP: Record<DocStatus, { label: string; bg: string; color: string }> = {
  numbered: { label: 'NUMERADA', bg: '#dcfce7', color: '#166534' },
  available: { label: 'NUMERAÇÃO DISPONÍVEL', bg: '#dcfce7', color: '#166534' },
  missing: { label: 'SOLICITAR NUMERAÇÃO', bg: '#fee2e2', color: '#b91c1c' },
};

function DocumentCard({ doc, status, multiMonth }: { doc: SncrPlanDocument; status: DocStatus; multiMonth: boolean }) {
  const chip = STATUS_CHIP[status];
  const missing = status === 'missing';
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: `1px solid ${missing ? '#fecaca' : CLOUD_API_THEME.borderSoft}`,
        bgcolor: missing ? '#fef2f2' : CLOUD_API_THEME.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: (doc.items || []).length ? 1.25 : 0 }}>
        <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '0.85rem', color: CLOUD_API_THEME.navy }}>
          {SNCR_TIPO_LABELS[doc.receita_tipo]}
          {multiMonth && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: CLOUD_API_THEME.slate }}>
              mês {doc.month_index + 1}
            </Typography>
          )}
        </Typography>
        <Chip
          size="small"
          label={doc.numero ? `Nº ${doc.numero}` : chip.label}
          sx={{ height: 22, bgcolor: chip.bg, color: chip.color, fontWeight: 700, fontSize: '0.62rem', letterSpacing: 0.3 }}
        />
      </Box>
      <Stack spacing={0.75}>
        {(doc.items || []).map((item, idx) => (
          <Box key={item.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {item.control_class && (
              <Chip
                size="small"
                label={item.control_class}
                sx={{ height: 20, minWidth: 30, bgcolor: '#e2e8f0', color: CLOUD_API_THEME.navy, fontWeight: 700, fontSize: '0.62rem' }}
              />
            )}
            <Typography sx={{ fontSize: '0.8rem', color: CLOUD_API_THEME.navy }}>{item.label}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function formatIssueDate(value?: string | null): string {
  const raw = String(value || '').split('T')[0];
  const [y, m, d] = raw.split('-');
  return y && m && d ? `${d}/${m}/${y}` : '';
}

function todayYmd(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface PrescriptionGroup {
  key: string;
  number: string | null;
  issueDate: string | null;
  entries: Array<{ doc: SncrPlanDocument; status: DocStatus }>;
}

/** Lote: um bloco por receita, na ordem das datas de emissão. */
function groupByPrescription(plan: SncrPrescriptionPlan, statuses: DocStatus[]): PrescriptionGroup[] {
  const groups = new Map<string, PrescriptionGroup>();
  plan.documents.forEach((doc, idx) => {
    const key = doc.prescription_id || 'single';
    if (!groups.has(key)) {
      groups.set(key, { key, number: doc.prescription_number || null, issueDate: doc.issue_date || null, entries: [] });
    }
    groups.get(key)!.entries.push({ doc, status: statuses[idx] });
  });
  return Array.from(groups.values());
}

interface Props {
  plan: SncrPrescriptionPlan;
  error?: string | null;
  notice?: string | null;
  printingPhysical?: boolean;
  onRequestNumbering: () => void;
  onContinueDigital: () => void;
  /** Ausente no lote: o talão físico é impresso receita a receita, pela lista. */
  onContinuePhysical?: () => void;
  onClose: () => void;
}

const SncrAnvisaEmissionStep: React.FC<Props> = ({
  plan,
  error,
  notice,
  printingPhysical,
  onRequestNumbering,
  onContinueDigital,
  onContinuePhysical,
  onClose,
}) => {
  const [howOpen, setHowOpen] = useState(false);
  const statuses = useMemo(() => docStatuses(plan), [plan]);
  const groups = useMemo(() => groupByPrescription(plan, statuses), [plan, statuses]);
  const isBatch = groups.length > 1;
  const today = todayYmd();
  const anyMissing = statuses.includes('missing');
  const multiMonth = plan.documents.some((d) => d.month_index > 0);
  const notificationMissing = plan.needs.some((n) => n.missing > 0 && isSncrNotificationTipo(n.receita_tipo));
  const canSignWithoutNumber = !plan.enforce && !notificationMissing;

  return (
    <>
      <DialogContent sx={{ ...cloudApiDialogContentSx, p: { xs: 2.5, sm: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 400, color: CLOUD_API_THEME.navy, mb: 1 }}>
            Emitir receita com numeração Anvisa
          </Typography>
          <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
            Receitas de medicamentos controlados precisam de numeração oficial da Anvisa para emissão digital.
            {isBatch
              ? ` São ${groups.length} receitas neste lote: a numeração é reservada para todas antes da assinatura, na ordem das datas.`
              : ' Confira abaixo suas numerações disponíveis para concluir a prescrição.'}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{notice}</Alert>}
        {plan.environment === 'hmg' && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Ambiente de homologação: os números não valem em farmácia.
          </Alert>
        )}

        <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: CLOUD_API_THEME.navy, mb: 1.25 }}>
          Receitas que exigem numeração Anvisa
        </Typography>
        {isBatch ? (
          <Stack spacing={2}>
            {groups.map((group) => {
              const date = formatIssueDate(group.issueDate);
              const future = Boolean(group.issueDate && String(group.issueDate).split('T')[0] > today);
              return (
                <Box key={group.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: CLOUD_API_THEME.navy }}>
                      {group.number ? `Receituário ${group.number}` : 'Receituário'}
                      {date ? ` · ${date}` : ''}
                    </Typography>
                    {future && (
                      <Chip
                        size="small"
                        label="Data programada"
                        sx={{ height: 20, bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600, fontSize: '0.62rem' }}
                      />
                    )}
                  </Box>
                  <Stack spacing={1}>
                    {group.entries.map(({ doc, status }) => (
                      <DocumentCard key={`${group.key}-${doc.slot}`} doc={doc} status={status} multiMonth={multiMonth} />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Stack spacing={1.25}>
            {plan.documents.map((doc, idx) => (
              <DocumentCard key={doc.slot} doc={doc} status={statuses[idx]} multiMonth={multiMonth} />
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Accordion
          expanded={howOpen}
          onChange={() => setHowOpen((v) => !v)}
          disableGutters
          elevation={0}
          sx={{ border: `1px solid ${CLOUD_API_THEME.borderSoft}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: CLOUD_API_THEME.navy }}>
              Como funciona a solicitação de numeração?
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0, color: CLOUD_API_THEME.slate, fontSize: '0.8rem', lineHeight: 1.55 }}>
              <li>O prescritor entra com a própria conta Gov.br (no computador ou pelo celular, via QR Code).</li>
              <li>
                O Klinvia pede os números ao SNCR da Anvisa: Controle Especial e Retenção vêm em lotes de 1.000
                (até 3 pedidos por mês); notificações (A, B, B2, retinoides, talidomida) dependem de saldo
                autorizado pela vigilância sanitária local.
              </li>
              <li>Os números ficam guardados para as próximas receitas — não é preciso pedir a cada prescrição.</li>
              <li>Com a numeração recebida, a receita segue para a assinatura digital ICP-Brasil e sai no modelo oficial.</li>
              <li>Primeiro acesso? Entre uma vez em sncr.anvisa.gov.br para ativar o cadastro do prescritor.</li>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {onContinuePhysical ? (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: CLOUD_API_THEME.navy, mb: 0.5 }}>
              Prefere continuar com receituário físico?
            </Typography>
            <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate }}>
              Você também pode continuar com a emissão da receita física utilizando seu receituário tradicional.
              {canSignWithoutNumber && anyMissing
                ? ' Neste período de transição também é possível assinar digitalmente sem número SNCR.'
                : ''}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 2.5, color: CLOUD_API_THEME.slate }}>
            Para usar o talão físico, cancele e imprima as receitas pela lista de receituários.
            {canSignWithoutNumber && anyMissing
              ? ' Neste período de transição também é possível assinar digitalmente sem número SNCR.'
              : ''}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ ...cloudApiDialogFooterSx, p: 3, gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button onClick={onClose} sx={cloudApiDialogCancelButtonSx}>
          Cancelar
        </Button>
        {onContinuePhysical && (
          <Button
            onClick={onContinuePhysical}
            disabled={printingPhysical}
            startIcon={printingPhysical ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ ...cloudApiDialogCancelButtonSx, bgcolor: '#e5e7eb', '&:hover': { bgcolor: '#d1d5db' } }}
          >
            Continuar sem numeração
          </Button>
        )}
        {anyMissing ? (
          <>
            {canSignWithoutNumber && (
              <Button onClick={onContinueDigital} sx={cloudApiDialogCancelButtonSx}>
                Assinar sem número
              </Button>
            )}
            <Button variant="contained" onClick={onRequestNumbering} sx={cloudApiDialogPrimaryButtonSx}>
              Solicitar numeração digital
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onContinueDigital} sx={cloudApiDialogPrimaryButtonSx}>
            Continuar para assinatura
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default SncrAnvisaEmissionStep;
